import type { Location } from '@jobos/shared/types/location';

export function formatLocationDisplay(
  location: Pick<Location, 'name' | 'addressLine1' | 'addressLine2' | 'city'>
): string {
  const parts: string[] = [];
  if (location.name) parts.push(location.name);
  if (location.addressLine1) parts.push(location.addressLine1);
  if (location.addressLine2) parts.push(location.addressLine2);
  if (location.city) parts.push(location.city);
  return parts.join(', ');
}

export function getGroupKey(location: Location): string {
  const parts: string[] = [];
  if (location.addressLine1) parts.push(location.addressLine1);
  if (location.city) parts.push(location.city);
  return parts.join(', ') || location.name || 'Unknown';
}

export function getItemLabel(location: Location): string {
  // Always prefer short form since items are rendered under a group heading
  // that already shows the building address
  const parts: string[] = [];
  if (location.name) parts.push(location.name);
  if (location.addressLine2) parts.push(location.addressLine2);
  if (parts.length === 0) {
    return 'Main address';
  }
  return parts.join(' \u2014 ');
}

export interface LocationGroup {
  key: string;
  heading: string;
  locations: Location[];
}

function compareByLeadingNumber(a: string, b: string): number {
  const numA = parseInt(a, 10);
  const numB = parseInt(b, 10);
  if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
  if (!isNaN(numA)) return -1;
  if (!isNaN(numB)) return 1;
  return a.localeCompare(b);
}

export function groupLocations(locations: Location[]): LocationGroup[] {
  const groupMap = new Map<string, Location[]>();

  for (const location of locations) {
    const key = getGroupKey(location);
    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)!.push(location);
  }

  return Array.from(groupMap.entries())
    .sort(([a], [b]) => compareByLeadingNumber(a, b))
    .map(([key, locs]) => ({
      key,
      heading: key,
      locations: locs.sort((a, b) =>
        compareByLeadingNumber(getItemLabel(a), getItemLabel(b))
      ),
    }));
}
